import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDebouncedValue } from 'new/hooks/useDebouncedValue';
import Constants from 'js/Constants';
import type { StatusFilter } from '../utils/toolLabels';

const ITEMS_PER_PAGE = Constants.LISTING_ITEMS_PER_PAGE;

interface UseListFilteringOptions<T> {
  items: T[];
  searchFields: (item: T) => (string | undefined)[];
}

interface UseListFilteringReturn<T> {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (filter: StatusFilter) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  filteredItems: T[];
  paginatedItems: T[];
  totalItems: number;
  totalPages: number;
  safePage: number;
  handlePageChange: (action: { type: string; value: number }) => void;
  statusCounts: { all: number; active: number; paused: number; draft: number };
  clearFilters: () => void;
}

export function useListFiltering<T extends { status?: string }>({
  items,
  searchFields,
}: UseListFilteringOptions<T>): UseListFilteringReturn<T> {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Store searchFields in ref to avoid re-memoization on every render
  // (callers typically pass inline arrow functions)
  const searchFieldsRef = useRef(searchFields);
  searchFieldsRef.current = searchFields;

  const filteredItems = useMemo(() => {
    let result = items;

    if (statusFilter !== 'all') {
      const statusValue = statusFilter === 'draft' ? 'init' : statusFilter;
      result = result.filter((item) => item.status === statusValue);
    }

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      result = result.filter((item) =>
        searchFieldsRef.current(item).some((field) => field?.toLowerCase().includes(query)),
      );
    }

    return result;
  }, [items, debouncedSearchQuery, statusFilter]);

  // Client-side pagination
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, statusFilter]);

  // Clamp currentPage when totalPages shrinks (e.g., after delete).
  // Uses functional update so this batched setState doesn't overwrite
  // the page-1 reset above when both effects fire in the same commit.
  useEffect(() => {
    setCurrentPage((prev) => (prev > totalPages ? totalPages : prev));
  }, [totalPages]);

  const safePage = Math.min(currentPage, totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, safePage]);

  const handlePageChange = useCallback((action: { type: string; value: number }) => {
    if (action.type === 'updateCurrentPage') {
      setCurrentPage(action.value);
    }
  }, []);

  // Status filter counts
  const statusCounts = useMemo(() => {
    const counts = { all: items.length, active: 0, paused: 0, draft: 0 };
    items.forEach((item) => {
      if (item.status === 'active') counts.active++;
      else if (item.status === 'paused') counts.paused++;
      else if (item.status === 'init') counts.draft++;
    });
    return counts;
  }, [items]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    filteredItems,
    paginatedItems,
    totalItems,
    totalPages,
    safePage,
    handlePageChange,
    statusCounts,
    clearFilters,
  };
}
